import { GUIDE_WIDTH, NAVIGATION_WIDTH, RAIL_WIDTH } from "../caseLayout.ts";

/**
 * What a page's guide holds. Its texts are in `i18n/guide.ts`, under the keys
 * listed here.
 */
export type GuidePage = {
  // the least width of the page, its widest table with padding: the open
  // guide never squeezes it below this
  readonly minWidth: number;
  // the entries tied to an element of the page, in the order the elements
  // appear; a marker's number is the place of its entry here
  readonly entries: readonly string[];
  // the entries about the page as a whole, with no element to point at
  readonly general: readonly string[];
  // entries of an element the page shows only in some cases: listed only
  // while it is there, as an entry for nothing to be seen only confuses
  readonly conditional?: readonly string[];
};

/** The configuration pages, each with its guide, by their path in a case. */
export const GUIDE_PAGES = {
  parties: {
    minWidth: 650,
    entries: ["client", "assets"],
    general: ["effect", "removal"],
  },
  timeline: {
    minWidth: 936,
    entries: ["dates", "duration", "ended", "status"],
    general: ["effect", "standing"],
  },
  claims: {
    minWidth: 958,
    entries: [
      "parties",
      "settled",
      "chances",
      "kind",
      "relation",
      "summary",
      "amounts",
      "liability",
    ],
    general: ["effect", "type", "counterclaims"],
  },
  costs: {
    minWidth: 820,
    entries: ["editor", "vat", "progress", "breakdown"],
    general: ["effect", "settlements", "settlementCosts"],
  },
  costStructure: {
    minWidth: 620,
    entries: ["indirect", "direct", "profit"],
    general: ["clients", "missing"],
  },
  agreements: {
    minWidth: 820,
    entries: ["lawFirmCfa", "covered", "lost", "success", "trigger", "barrister"],
    general: ["recovery", "vat", "mocks"],
  },
  settlement: {
    minWidth: 980,
    entries: ["chances", "position", "ifFirst"],
    general: ["adjusted", "offerAhead", "sequences", "shared"],
    // the range position only stands where no offer is on the table, and
    // "if another settles first" only on an offer that has that case
    conditional: ["position", "ifFirst"],
  },
  recoveries: {
    minWidth: 872,
    entries: ["tabs", "awarded", "total", "recovery"],
    general: ["bothWays", "paid", "vat", "settlements"],
  },
  apportionments: {
    minWidth: 760,
    entries: ["shares", "generic", "modes", "liability", "bargaining"],
    general: ["settled"],
  },
} as const satisfies { [page: string]: GuidePage };

export type GuidePageId = keyof typeof GUIDE_PAGES;

const isGuidePage = (path: string): path is GuidePageId =>
  Object.prototype.hasOwnProperty.call(GUIDE_PAGES, path);

/** The page of a case a location leads to, where that page has a guide. */
export function guidePageOf(pathname: string): GuidePageId | undefined {
  const [, page] = /\/configuration\/([^/]+)\/?$/.exec(pathname) ?? [];
  return page !== undefined && isGuidePage(page) ? page : undefined;
}

/**
 * The widths of the window the open guide needs: `beside`, to stand beside the
 * page with the navigation in full, and `besideRail`, with the navigation
 * folded to its rail. Below that it lies over the page.
 */
export const guideWidths = ({ minWidth }: GuidePage) => ({
  beside: NAVIGATION_WIDTH + minWidth + GUIDE_WIDTH,
  besideRail: RAIL_WIDTH + minWidth + GUIDE_WIDTH,
});

/** The entries a page's guide lists: of a conditional element, only while it is there. */
export function shownEntries(
  { entries, conditional = [] }: GuidePage,
  present: ReadonlySet<string>,
): readonly string[] {
  return entries.filter(
    (entry) => !conditional.includes(entry) || present.has(entry),
  );
}
